import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import FusionLogo from '../components/FusionLogo';
import SupplierFooter from '../components/SupplierFooter';
import { formatDateBritish, docLabelFor } from '../lib/compliance';
import { loadDocumentStream, supplierVisibleStream, supplierEventLabel } from '../lib/complianceEvents';

// ============================================================================
// The supplier's own document history (detail-page brief §1 — "both sides see
// the same stream")
// ============================================================================
// Admins have had this since the detail page shipped; the supplier had a status
// and a comment box, and no way to see what had actually happened to a document
// they'd sent in weeks ago. This is the same chronological stream, filtered to
// what is theirs to see (supplierVisibleStream) and worded in the second person.
//
// Read-only by design. Everything they can DO — upload, reply, replace — is on
// the compliance page they came from, and duplicating it here would give two
// places to do the same thing badly. This page answers "what happened?", not
// "what now?".
// ============================================================================

const fmtDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${formatDateBritish(d.toISOString().slice(0, 10))}, ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const TONE = {
  approved: '#3B6D11',
  expiry_approved: '#3B6D11',
  acknowledged: '#3B6D11',
  rejected: '#B71F06',
  clarification_requested: '#7A4E10',
  uploaded: '#2E8FA1',
  replied: '#2E8FA1',
  superseded: '#51534A',
  required: '#51534A',
};

export default function SupplierDocumentHistory() {
  const { documentKey } = useParams();
  const navigate = useNavigate();

  const [supplier, setSupplier] = useState(null);
  const [label, setLabel] = useState('');
  const [stream, setStream] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setError('Please sign in again.'); setLoading(false); return; }

    // The supplier this session belongs to. RLS scopes everything below to
    // them, but resolving it here means the page can say whose history it is
    // rather than showing an empty stream if something is off.
    //
    // supplier_users.id IS the auth user id — the row shares the primary key
    // with auth.users, it doesn't carry a separate auth_user_id column. This
    // matched on a column that doesn't exist, so every supplier opening
    // Document history was told we couldn't find their account. Same lookup
    // as SupplierPortal and SupplierCompliance.
    const { data: su, error: suError } = await supabase
      .from('supplier_users')
      .select('supplier_id, suppliers(supplier_name)')
      .eq('id', user.id)
      .maybeSingle();
    // Say WHICH failure it was. A PostgREST error (bad column, RLS refusal) is
    // a different problem from a signed-in user with no supplier row, and the
    // one generic message hid the former as the latter.
    if (suError) { setError(`Couldn't load your account: ${suError.message}`); setLoading(false); return; }
    if (!su?.supplier_id) { setError('We couldn\'t find your supplier account.'); setLoading(false); return; }
    setSupplier({ id: su.supplier_id, name: su.suppliers?.supplier_name });

    const [{ data: fd }, rows] = await Promise.all([
      supabase.from('fusion_documents')
        .select('document_name, compliance_doc_key, status')
        .eq('compliance_doc_key', documentKey),
      loadDocumentStream(su.supplier_id, documentKey),
    ]);
    const live = (fd || []).find(d => d.status === 'active') || (fd || [])[0];
    setLabel(docLabelFor(live?.document_name, documentKey));
    setStream(supplierVisibleStream(rows));
    setLoading(false);
  }, [documentKey]);

  useEffect(() => { load(); }, [load]);

  // Oldest first: this reads as the story of the document, and the supplier is
  // usually here to work out how it got to where it is.
  const ordered = [...stream].reverse();

  return (
    <div className="min-h-screen bg-canvas">
      <div className="bg-surface-sidebar border-b border-white/[0.08]">
        <div className="max-w-3xl mx-auto px-4 sm:px-10 h-[76px] sm:h-[90px] flex items-center">
          <button type="button" onClick={() => navigate('/portal')} aria-label="Fusion 2K Supply Pilot — home" title="Home"
            className="flex items-center gap-2.5 bg-transparent border-0 p-0 cursor-pointer hover:opacity-80 transition-opacity">
            <FusionLogo icon alt="Fusion" className="h-[38px] w-auto object-contain" />
            <span className="hidden sm:inline-flex items-center whitespace-nowrap font-['Metropolis'] text-[12px] font-bold tracking-[0.16em] uppercase text-sb-text">
              Supply Pilot <span className="text-sb-muted font-semibold">&nbsp;·&nbsp; Partner Portal</span>
            </span>
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-8 pb-16">
        <button
          type="button"
          onClick={() => navigate('/portal/compliance')}
          className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.18em] text-[#51534A] hover:text-fusion-mid-grey inline-flex items-center gap-1.5 mt-4 mb-6">
          <span aria-hidden="true">←</span> Back to compliance
        </button>

        <h1 className="font-['Metropolis'] text-[26px] sm:text-[30px] font-extrabold tracking-tight text-ink-primary mb-1">
          {label || 'Document history'}
        </h1>
        <p className="font-['Metropolis'] text-[13px] text-fusion-mid-grey leading-relaxed mb-8">
          Everything that's happened with this document{supplier?.name ? ` for ${supplier.name}` : ''} — what you sent,
          what Fusion 2K decided, and anything either of you said about it. To upload or reply, go back to your
          compliance page.
        </p>

        {loading ? (
          <p className="font-['Metropolis'] text-[13px] text-fusion-mid-grey">Loading…</p>
        ) : error ? (
          <p className="font-['Metropolis'] text-[13px] text-[#E0290A]">{error}</p>
        ) : ordered.length === 0 ? (
          <div className="bg-white border border-[#E6EADB] rounded-2xl p-6">
            <p className="font-['Metropolis'] text-[13px] text-fusion-mid-grey">
              Nothing has happened with this document yet. Once you upload it, everything that follows shows up here.
            </p>
          </div>
        ) : (
          <ol className="bg-white border border-[#E6EADB] rounded-2xl shadow-lift p-5 space-y-3.5">
            {ordered.map(item => {
              const mine = item.side === 'supplier';
              if (item.kind === 'message') {
                return (
                  <li key={item.id} className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
                    <div className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.14em] mb-1"
                      style={{ color: mine ? '#2E8FA1' : '#7A4E10' }}>
                      {mine ? 'You' : 'Fusion 2K'}{item.at ? ` · ${fmtDateTime(item.at)}` : ''}
                    </div>
                    <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 font-['Metropolis'] text-[13px] text-ink leading-snug whitespace-pre-wrap ${mine ? 'bg-[#2E8FA1]/[0.08]' : 'bg-[#E89A2E]/[0.08]'}`}>
                      {item.body}
                      {item.fileUrl && (
                        <a href={item.fileUrl} target="_blank" rel="noopener noreferrer"
                          className="block mt-1 font-semibold underline underline-offset-2 text-[#2E8FA1]">
                          {item.fileName || 'View attachment'}
                        </a>
                      )}
                    </div>
                  </li>
                );
              }
              const tone = TONE[item.type] || '#51534A';
              return (
                <li key={item.id} className="flex items-start gap-3">
                  <span aria-hidden="true" className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ background: tone }} />
                  <div className="min-w-0">
                    <div className="font-['Metropolis'] text-[13px] text-ink">
                      <span className="font-bold" style={{ color: tone }}>{supplierEventLabel(item)}</span>
                      <span className="text-fusion-mid-grey"> · {fmtDateTime(item.at)}</span>
                      {item.meta?.expiry_date && (
                        <span className="text-fusion-mid-grey"> · expires {formatDateBritish(item.meta.expiry_date)}</span>
                      )}
                    </div>
                    {item.body && (
                      <div className="font-['Metropolis'] text-[12px] text-[#51534A] mt-0.5 whitespace-pre-wrap">{item.body}</div>
                    )}
                    {item.type === 'uploaded' && item.fileUrl && (
                      <a href={item.fileUrl} target="_blank" rel="noopener noreferrer"
                        className="font-['Metropolis'] text-[11px] font-semibold underline underline-offset-2 text-[#2E8FA1]">
                        {item.fileName || 'View the file you sent'}
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <SupplierFooter />
    </div>
  );
}
